import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { ListItem, MenuGroup, MenuItem, Text } from "@ui-kitten/components";
import Button from "components/Button";
import Chevron from "components/Chevron";
import { LIGHT_BLUE } from "constants/";
import {
  BestTicketBlockDetails,
  SeatDetails,
  SectionDetails,
} from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { findSeatsBySectionAndRowRequest } from "queries/venue.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { isSectionDetails } from "../helpers";

interface Props {
  item: BestTicketBlockDetails | SectionDetails;
  selectingBestBlock: boolean;
  hideRating: boolean;
  color: string;
  rs: number;
  msg: string;
  shouldShowGuestPrice: boolean;
  includeAccessible: boolean;
  handleSectionPress: (section: SectionDetails) => void;
  handleBestBlockPress: (seats: SeatDetails[]) => void;
  positionInList: number; // In some cases we will have two ticket blocks with the same section and row, causing the seat squares not rendering
}

const SECTION_ROW_SEATS_CACHE: Record<string, SeatDetails[]> = {};

const getCacheKey = (
  venue: string,
  section: string,
  row: string,
  positionInList: number
) => {
  return `${venue}-${section}-${row}-${positionInList}`;
};

const SectionItem: React.FC<Props> = (props) => {
  const {
    item,
    selectingBestBlock,
    hideRating,
    color,
    msg,
    rs,
    shouldShowGuestPrice,
    includeAccessible,
    handleBestBlockPress,
    handleSectionPress,
    positionInList,
  } = props;
  const { data: branding } = useFetchTenantBrandingQuery();
  const { selectedPartySize, selectedEvent, selectedSubscription } =
    useAppSelector((state) => state.reservationFlow);

  const { data: seats, mutateAsync: findSeatsBySectionAndRow } = useMutation(
    findSeatsBySectionAndRowRequest
  );
  const allInclusivePrice =
    selectedSubscription?.planPackage.allInclusivePrice || false;
  const [checkedSeats, setCheckedSeats] = useState<SeatDetails[]>([]);

  const getPrice = (
    item: SectionDetails | BestTicketBlockDetails,
    baseChargeSelector: (
      item: SectionDetails | BestTicketBlockDetails
    ) => string,
    feesSelector: (item: SectionDetails | BestTicketBlockDetails) => string,
    allInclusivePrice: boolean
  ): number => {
    return (
      Number(baseChargeSelector(item)) +
      (allInclusivePrice ? Number(feesSelector(item)) : 0)
    );
  };

  const getTicketPrice = (item: SectionDetails | BestTicketBlockDetails) => {
    return getPrice(
      item,
      (item) =>
        isSectionDetails(item)
          ? item.minPlanTicketCharge || ""
          : item.perTicketCharge || "",
      (item) =>
        isSectionDetails(item)
          ? item.minPlanTicketFees || ""
          : item.perTicketFees || "",
      allInclusivePrice
    );
  };

  const getGuestTicketPrice = (
    item: SectionDetails | BestTicketBlockDetails
  ) => {
    return getPrice(
      item,
      (item) =>
        isSectionDetails(item)
          ? item.minPlanGuestCharge || ""
          : item.perGuestCharge || "",
      (item) =>
        isSectionDetails(item)
          ? item.minPlanGuestFees || ""
          : item.perGuestFees || "",
      allInclusivePrice
    );
  };

  const shouldShowRating = useMemo(() => {
    return !item.ga && !hideRating && selectingBestBlock;
  }, [item, hideRating, selectingBestBlock]);

  const renderTitle = useMemo(
    () => (
      <View className="flex flex-row justify-between items-center">
        <View className="column basis-2/3">
          <View>
            <Text style={styles.sectionTextStyle}>
              Section {item.sectionDisplay}{" "}
              {
                // if selectingBestBlock is true, then show the rowDisplay
                (selectingBestBlock
                  ? item.ga
                    ? ""
                    : `Row ${(item as BestTicketBlockDetails).rowDisplay} `
                  : null) as string
              }
              {
                // if item.accessible is true and includeAccessible is true, then show the wheelchair icon
                (item.accessible && includeAccessible && (
                  <MaterialCommunityIcons
                    name="wheelchair-accessibility"
                    size={20}
                    color={LIGHT_BLUE}
                  />
                )) as React.ReactElement
              }
            </Text>
          </View>
          {shouldShowRating && (
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  marginRight: 5,
                  borderRadius: 6,
                  paddingHorizontal: 6,
                  backgroundColor: color,
                  paddingVertical: 5,
                }}
              >
                <Text style={{ color: "white" }}>{rs}</Text>
              </View>
              <Text>{msg}</Text>
            </View>
          )}
        </View>

        <View>
          <Text style={{ fontSize: 14, fontWeight: "700" }}>
            {`${formatCurrency(getTicketPrice(item), {
              priceType: item.priceType,
            })}`}
            {shouldShowGuestPrice
              ? ` / ${formatCurrency(getGuestTicketPrice(item), {
                  priceType: item.priceType,
                })}`
              : ""}
          </Text>
        </View>
      </View>
    ),
    [item, selectingBestBlock, includeAccessible, hideRating, color, rs, msg]
  );

  const hasCachedSeats = (venue: string = "", section: string, row: string) => {
    const cachedKey = getCacheKey(venue, section, row, positionInList);
    return SECTION_ROW_SEATS_CACHE[cachedKey] !== undefined;
  };

  const setCachedSeats = (
    venue: string = "",
    section: string,
    row: string,
    seats: SeatDetails[]
  ) => {
    const cachedKey = getCacheKey(venue, section, row, positionInList);
    // If seats are flipped, reverse them - https://github.com/FanRally/fanrally-core/issues/1591
    if (seats.some((seat) => seat.flipped)) {
      SECTION_ROW_SEATS_CACHE[cachedKey] = seats.reverse();
    } else {
      SECTION_ROW_SEATS_CACHE[cachedKey] = seats;
    }
  };

  const getCacheSeats = (venue: string = "", section: string, row: string) => {
    const cachedKey = getCacheKey(venue, section, row, positionInList);
    return SECTION_ROW_SEATS_CACHE[cachedKey];
  };

  // if the section and row is not cached, then fetch the allSeatsInRow
  const handleMenuGroupPress = async () => {
    const section = item.section;
    const row = (item as BestTicketBlockDetails).row;
    if (hasCachedSeats(selectedEvent?.venueName, section, row)) {
      return;
    }
    const seatsForRow = await findSeatsBySectionAndRow({
      venueId: selectedEvent!.venueId,
      section,
      row,
      eventTypeName: selectedEvent!.eventType,
    });
    setCachedSeats(selectedEvent?.venueName, section, row, seatsForRow);
  };

  const cachedSeats = getCacheSeats(
    selectedEvent?.venueName,
    item.section,
    (item as BestTicketBlockDetails).row
  );

  const sortedSeats = useMemo(() => {
    const seats = [...checkedSeats];
    // If seats are flipped, reverse them - https://github.com/FanRally/fanrally-core/issues/1591
    if (seats.some((seat) => seat.flipped)) {
      return seats.sort((a, b) =>
        b.seatDisplay.localeCompare(a.seatDisplay, "en", { numeric: true })
      );
    }
    return seats.sort((a, b) =>
      a.seatDisplay.localeCompare(b.seatDisplay, "en", { numeric: true })
    );
  }, [checkedSeats]);

  const isSeatChecked = (seat: SeatDetails) =>
    checkedSeats.some((checkedSeat) => checkedSeat.id === seat.id);

  // Check if the seat is available in the best block
  const isSeatAvailable = (seat: SeatDetails) => {
    const seatAvailable = (item as BestTicketBlockDetails).tickets.some(
      (ticket) => ticket.seatId === seat.id
    );
    return seatAvailable;
  };

  // If checkedSeats.length is less than selectedPartySize, then add the seat to checkedSeats
  // If checkedSeats.length is equal to selectedPartySize, then remove the first seat from checkedSeats and add the new seat
  const handleCheckSeat = (seat: SeatDetails) => {
    if (!isSeatAvailable(seat)) return;
    if (checkedSeats.length < selectedPartySize) {
      setCheckedSeats([...checkedSeats, seat]);
    } else {
      setCheckedSeats([...checkedSeats.slice(1), seat]);
    }
  };

  const handleRemoveSeat = (seat: SeatDetails) => {
    setCheckedSeats(
      checkedSeats.filter((checkedSeat) => checkedSeat.id !== seat.id)
    );
  };

  // Check if the seat is unavailable meaning it is not available or it is already checked
  const isSeatCheckedOrUnavailable = (seat: SeatDetails) => {
    return isSeatChecked(seat) || !isSeatAvailable(seat);
  };

  const isSeatUncheckedAndAvailable = (seat: SeatDetails) => {
    return !isSeatChecked(seat) && isSeatAvailable(seat);
  };

  // Check if there are any dangling singles
  const hasDanglingSingles = useMemo(() => {
    if (!cachedSeats) return false;
    if (checkedSeats.length === 0) return false;
    if (checkedSeats.length < selectedPartySize) return false;
    const availableSeats = cachedSeats.filter(isSeatAvailable);

    /**
     * First check if the selectedPartySize is equal to the availableSeats.length - 1
     * For example, selectedPartySize is 4 and availableSeats.length is 5, in this case
     * dangling singles only happen if the first and last seat are both checked, like:
     * [-, -, -, -, O, O, O, O, O, -, -, -, -]
     * [-, -, -, -, X, X, O, X, X, -, -, -, -]
     */
    if (selectedPartySize === availableSeats.length - 1) {
      const firstInAvailableSeats = availableSeats[0];
      const lastInAvailableSeats = availableSeats[availableSeats.length - 1];
      if (
        !isSeatChecked(firstInAvailableSeats) ||
        !isSeatChecked(lastInAvailableSeats)
      ) {
        return false;
      }
    }

    /**
     * for all seats in a row, when we see a checked seat, and the previous seat is not checked and available:
     *  1. if the checked seat is the second seat, then there is a dangling single
     *  2. if the seat before the previous seat is checked or unavailable, then there is a dangling single
     */

    for (let i = 1; i < cachedSeats.length; i++) {
      if (isSeatChecked(cachedSeats[i])) {
        if (isSeatUncheckedAndAvailable(cachedSeats[i - 1])) {
          if (i === 1) return true;
          if (isSeatCheckedOrUnavailable(cachedSeats[i - 2])) return true;
        }
      }
    }

    // Perform the same check but in reverse
    for (let i = cachedSeats.length - 2; i >= 0; i--) {
      if (isSeatChecked(cachedSeats[i])) {
        if (isSeatUncheckedAndAvailable(cachedSeats[i + 1])) {
          if (i === cachedSeats.length - 2) return true;
          if (isSeatCheckedOrUnavailable(cachedSeats[i + 2])) return true;
        }
      }
    }
    return false;
  }, [checkedSeats, cachedSeats, selectedPartySize]);

  const renderLegendIcon = (seat: SeatDetails) => {
    if (seat.accessible) {
      return (
        <MaterialCommunityIcons name="wheelchair-accessibility" color="white" />
      );
    }
    if (seat.aisle) {
      return <Text status="control">A</Text>;
    }
  };

  // Only show the individual seat selector if it is not a GA section and the provider is not TFL
  const shouldShowSeatSelector =
    !item.ga &&
    selectingBestBlock &&
    (item as BestTicketBlockDetails).tickets[0].provider !== "TFL";

  const renderSeatSelector = () => {
    return (
      <View>
        <View>
          <Text style={{ fontWeight: "700" }}>
            Selected seats:{" "}
            {sortedSeats
              .map((seat) => {
                if (seat.aisle) {
                  return seat.seatDisplay + " (aisle)";
                }
                return seat.seatDisplay;
              })
              .join(", ")}
          </Text>
          <ScrollView horizontal className="mt-2">
            {cachedSeats ? (
              <>
                {cachedSeats.map((seat) =>
                  isSeatChecked(seat) ? (
                    <Pressable
                      key={seat.id}
                      className="h-5 w-5 rounded-full flex items-center justify-center"
                      // background color is the same as the rankscore color
                      style={{
                        backgroundColor: "#69b34c",
                      }}
                      onPress={() => handleRemoveSeat(seat)}
                    >
                      <MaterialCommunityIcons
                        name="check"
                        size={15}
                        color="white"
                      />
                    </Pressable>
                  ) : (
                    <Pressable
                      key={seat.id}
                      onPress={() => handleCheckSeat(seat)}
                      style={{
                        backgroundColor: isSeatAvailable(seat)
                          ? branding?.primaryColor
                          : "#ebebeb",
                      }}
                      className="h-5 w-5 rounded-full justify-center items-center"
                    >
                      {renderLegendIcon(seat)}
                    </Pressable>
                  )
                )}
              </>
            ) : (
              // placeholder
              <View className="h-5 w-5"></View>
            )}
          </ScrollView>
          <Text
            style={{ minHeight: 30, marginTop: 4 }}
            status="danger"
            category="c1"
          >
            {hasDanglingSingles
              ? "This venue won't allow an empty seat between you and another guest. Please choose another seat."
              : ""}
          </Text>
        </View>

        <Button
          testID="reserve-seats-button"
          className="mt-2"
          onPress={() => handleBestBlockPress(checkedSeats)}
          disabled={
            checkedSeats.length !== selectedPartySize || hasDanglingSingles
          }
        >
          Reserve
        </Button>
      </View>
    );
  };
  // Automatically select the first n tickets
  useEffect(() => {
    if (!selectingBestBlock) return;
    const preSelectedSeats = [];

    for (let i = 0; i < selectedPartySize; i++) {
      const seat = (item as BestTicketBlockDetails).tickets[i];
      if (!seat) break;
      preSelectedSeats.push({
        id: seat.seatId,
        section: seat.section,
        row: seat.row,
        seat: seat.seat,
        seatDisplay: seat.seatDisplay,
        aisle: seat.aisle,
        flipped: seat.flipped,
      } as SeatDetails);
    }
    setCheckedSeats(preSelectedSeats);
  }, [selectedPartySize, selectingBestBlock, item, seats]);

  return (
    <View>
      {selectingBestBlock ? (
        <>
          {shouldShowSeatSelector ? (
            <MenuGroup
              title={renderTitle}
              accessoryRight={<Chevron direction="down" />}
              onPress={handleMenuGroupPress}
              testID="section-item-block"
            >
              <MenuItem title={renderSeatSelector()}></MenuItem>
            </MenuGroup>
          ) : (
            <ListItem
              title={renderTitle}
              accessoryRight={<Chevron direction="right" />}
              onPress={() => handleBestBlockPress(checkedSeats)}
            ></ListItem>
          )}
        </>
      ) : (
        <ListItem
          title={renderTitle}
          accessoryRight={<Chevron direction="right" />}
          onPress={() => handleSectionPress(item as SectionDetails)}
        ></ListItem>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  sectionTextStyle: {
    fontSize: 16,
    fontWeight: "700",
  },
});
export default SectionItem;
